import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * PushNotificationPrompt - Shows contextually after relevant user actions
 * 
 * Triggers:
 * - After placing a bid (auction)
 * - After sending a message
 * - After saving a listing as favorite
 * 
 * Usage: Dispatch custom event from anywhere in the app:
 * window.dispatchEvent(new CustomEvent('triggerPushPrompt', { detail: { action: 'bid' } }));
 */
const PushNotificationPrompt = ({ user }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [triggerAction, setTriggerAction] = useState(null);
  const hasShownThisSessionRef = useRef(false);
  const { language } = useLanguage();
  
  const {
    permission,
    isSupported,
    isSubscribed,
    isLoading,
    iosInfo,
    subscribe
  } = usePushNotifications(user);

  const translations = {
    en: {
      title: 'Enable Notifications',
      description: 'Get instant alerts for new messages, bids on your listings, and updates on your favorites.',
      bidContext: 'Get notified when someone outbids you!',
      messageContext: 'Get notified when they reply!',
      favoriteContext: 'Get notified when the price drops!',
      benefit1: 'New messages',
      benefit2: 'Outbid alerts',
      benefit3: 'Price updates',
      later: 'Later',
      enable: 'Enable',
      enabling: 'Enabling...',
      neverAsk: "Don't ask again"
    },
    nl: {
      title: 'Notificaties Inschakelen',
      description: 'Ontvang direct een melding bij nieuwe berichten, biedingen en updates op je favorieten.',
      bidContext: 'Krijg een melding als iemand je overbiedt!',
      messageContext: 'Krijg een melding als ze antwoorden!',
      favoriteContext: 'Krijg een melding als de prijs daalt!',
      benefit1: 'Nieuwe berichten',
      benefit2: 'Overboden meldingen',
      benefit3: 'Prijs updates',
      later: 'Later',
      enable: 'Inschakelen',
      enabling: 'Bezig...',
      neverAsk: 'Niet meer vragen'
    },
    es: {
      title: 'Activar Notificaciones',
      description: 'Recibe alertas de nuevos mensajes, pujas en tus anuncios, y actualizaciones de tus favoritos.',
      bidContext: '¡Te avisamos si alguien te supera!',
      messageContext: '¡Te avisamos cuando respondan!',
      favoriteContext: '¡Te avisamos cuando baje el precio!',
      benefit1: 'Nuevos mensajes',
      benefit2: 'Alertas de pujas',
      benefit3: 'Cambios de precio',
      later: 'Después',
      enable: 'Activar',
      enabling: 'Activando...',
      neverAsk: 'No preguntar más'
    }
  };

  const t = translations[language] || translations.en;

  // Get contextual message based on trigger action
  const getContextMessage = () => {
    switch (triggerAction) {
      case 'bid':
        return t.bidContext;
      case 'message':
        return t.messageContext;
      case 'favorite':
        return t.favoriteContext;
      default:
        return t.description;
    }
  };

  // Listen for trigger events from other components
  useEffect(() => {
    const handleTrigger = (event) => {
      // Only trigger for logged-in users
      if (!user) return;
      
      // Don't show if already shown this session
      if (hasShownThisSessionRef.current) return;
      
      // Check localStorage for permanent dismissal
      const permanentlyDismissed = localStorage.getItem('push_prompt_dismissed_permanently');
      if (permanentlyDismissed) return;
      
      // Don't show if already subscribed or not supported or permission denied
      if (!isSupported || isSubscribed || permission === 'denied') return;
      
      // Check if running as PWA on iOS
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true;
      if (iosInfo?.isIOS && !isPWA) return;
      
      // Show the prompt with context
      const action = event.detail?.action || 'general';
      setTriggerAction(action);
      setShowPrompt(true);
      hasShownThisSessionRef.current = true;
    };

    window.addEventListener('triggerPushPrompt', handleTrigger);
    return () => window.removeEventListener('triggerPushPrompt', handleTrigger);
  }, [user, isSupported, isSubscribed, permission, iosInfo]);

  // Body scroll lock when prompt is visible
  useEffect(() => {
    if (showPrompt) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showPrompt]);

  // Hide prompt if user becomes subscribed
  useEffect(() => {
    if (isSubscribed && showPrompt) {
      setShowPrompt(false);
    }
  }, [isSubscribed, showPrompt]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const success = await subscribe();
      if (success) {
        localStorage.setItem('push_prompt_dismissed_permanently', 'true');
        setShowPrompt(false);
      }
    } catch (err) {
      console.error('Failed to enable push:', err);
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  const handleNeverAsk = () => {
    setShowPrompt(false);
    localStorage.setItem('push_prompt_dismissed_permanently', 'true');
  };

  if (!showPrompt || isLoading) return null;

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
            <Bell className="w-8 h-8 text-white" />
          </div>
          
          {/* Title & Description */}
          <h3 className="font-bold text-xl text-foreground mb-2">
            {t.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-5">
            {getContextMessage()}
          </p>
          
          {/* Benefits */}
          <div className="w-full space-y-2 mb-5 text-left">
            <div className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/50">
              <span className="text-lg">💬</span>
              <span className="text-sm text-foreground">{t.benefit1}</span>
            </div>
            <div className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/50">
              <span className="text-lg">🔔</span>
              <span className="text-sm text-foreground">{t.benefit2}</span>
            </div>
            <div className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/50">
              <span className="text-lg">💰</span>
              <span className="text-sm text-foreground">{t.benefit3}</span>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 w-full">
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-3 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl transition-colors text-sm font-medium"
              data-testid="dismiss-push-btn"
            >
              {t.later}
            </button>
            <button
              onClick={handleEnable}
              disabled={enabling}
              className="flex-1 px-4 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 transition-all text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="enable-push-btn"
            >
              {enabling ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t.enabling}</span>
                </>
              ) : (
                t.enable
              )}
            </button>
          </div>
          
          {/* Never ask */}
          <button
            onClick={handleNeverAsk}
            className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t.neverAsk}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to trigger the push prompt from anywhere
export const triggerPushPrompt = (action = 'general') => {
  window.dispatchEvent(new CustomEvent('triggerPushPrompt', { detail: { action } }));
};

export default PushNotificationPrompt;
